import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of, delay } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MOCK_DATA } from './mock-data';

/**
 * 🎭 Mock API Interceptor
 *
 * Intercepte les requêtes HTTP et retourne des données mockées quand environment.mockApi = true
 * Utile pour le développement sans backend
 */
export const mockApiInterceptor: HttpInterceptorFn = (req, next) => {
  // Si mockApi n'est pas activé, passer la requête normalement
  if (!environment.mockApi) {
    return next(req);
  }

  // Extraire le chemin de l'URL (sans le domaine/port)
  const url = new URL(req.url, window.location.origin);
  const path = url.pathname;
  const method = req.method;

  // Router vers les bonnes données mockées
  const mockResponse = getMockResponse(method, path, req.body);

  if (mockResponse) {
    const delayMs = Math.random() * 150 + 150;

    // 📡 SIMULATION d'appel réseau avec tous les détails
    console.group(`📡 [MOCK NETWORK] ${method} ${path}`);
    console.log('🔹 URL complète:', req.url);
    console.log('🔹 Méthode:', method);
    if (req.body) {
      console.log('🔹 Payload:', req.body);
    }
    console.log(
      '🔹 Headers:',
      req.headers.keys().map((k) => `${k}: ${req.headers.get(k)}`),
    );
    console.log(`⏱️ Délai simulé: ${Math.round(delayMs)}ms`);
    console.log('✅ Réponse mockée:', mockResponse);
    console.groupEnd();

    // Simuler un délai réseau (150-300ms)
    return of(
      new HttpResponse({
        status: 200,
        body: mockResponse,
      }),
    ).pipe(delay(delayMs));
  }

  // Si pas de mock trouvé, passer la vraie requête
  console.warn(
    '⚠️ [MOCK] No mock found for:',
    method,
    path,
    '- Using real API',
  );
  return next(req);
};

/**
 * Route les requêtes vers les bonnes données mockées
 */
function getMockResponse(method: string, path: string, body?: any): any {
  // Auth endpoints
  if (path.includes('/api/auth/me')) {
    return MOCK_DATA.auth.me;
  }
  if (path.includes('/api/auth/login')) {
    return MOCK_DATA.auth.login;
  }
  if (path.includes('/api/auth/register')) {
    return MOCK_DATA.auth.register;
  }
  if (path.includes('/api/auth/verify-email')) {
    return MOCK_DATA.auth.verifyEmail;
  }

  // Dashboard endpoints
  if (path.includes('/api/dashboard/kpis')) {
    return MOCK_DATA.dashboard.kpis;
  }
  if (path.includes('/api/dashboard/actions/pending')) {
    return MOCK_DATA.dashboard.pendingActions;
  }
  if (path.includes('/api/dashboard/fixed/predictions')) {
    return MOCK_DATA.dashboard.predictions;
  }
  if (path.includes('/api/dashboard/fixed/trends/weekly')) {
    return MOCK_DATA.dashboard.weeklyTrends;
  }

  // Dashboard itinerant endpoints (new — DashboardItinerantService)
  if (path.includes('/api/dashboard/itinerant/kpis')) {
    return MOCK_DATA.dashboard.itinerant.kpis;
  }
  if (path.includes('/api/dashboard/itinerant/events/by-status')) {
    return MOCK_DATA.dashboard.itinerant.eventsByStatus;
  }
  if (path.includes('/api/dashboard/itinerant/timeline')) {
    return MOCK_DATA.dashboard.itinerant.timeline;
  }
  if (path.includes('/api/dashboard/itinerant/weather-alerts')) {
    return MOCK_DATA.dashboard.itinerant.weatherAlerts;
  }
  if (path.includes('/api/dashboard/itinerant/insights')) {
    return MOCK_DATA.dashboard.itinerant.insights;
  }
  if (path.includes('/api/dashboard/itinerant/top-events')) {
    return MOCK_DATA.dashboard.itinerant.topEvents;
  }
  if (path.includes('/api/dashboard/itinerant/opportunities')) {
    return MOCK_DATA.dashboard.itinerant.opportunities;
  }
  if (path.includes('/api/dashboard/itinerant/recommendations/')) {
    const eventId = path.split('/recommendations/')[1];
    return (
      (MOCK_DATA.dashboard.itinerant as any).recommendations?.[eventId] || {
        eventId,
        eventName: '',
        products: [],
      }
    );
  }
  if (path.includes('/api/dashboard/itinerant/precision-history')) {
    return (MOCK_DATA.dashboard.itinerant as any).precisionHistory || [];
  }
  if (path.includes('/api/dashboard/itinerant/checklist/')) {
    const eventId = path.split('/checklist/')[1];
    return (
      (MOCK_DATA.dashboard.itinerant as any).checklists?.[eventId] || {
        eventId,
        eventName: '',
        items: [],
      }
    );
  }

  // Dashboard itinerant endpoints (legacy)
  if (path.includes('/api/dashboard/itinerant/events/upcoming')) {
    return MOCK_DATA.dashboard.itinerant.upcomingEvents;
  }
  if (path.includes('/api/dashboard/itinerant/performance/by-type')) {
    return MOCK_DATA.dashboard.itinerant.eventPerformance;
  }
  if (path.includes('/api/dashboard/itinerant/stats/locations')) {
    return MOCK_DATA.dashboard.itinerant.locationStats;
  }

  // Events endpoints
  if (path.includes('/api/events') && method === 'GET') {
    if (path.match(/\/api\/events\/\d+$/)) {
      return MOCK_DATA.events.singleEvent;
    }
    return MOCK_DATA.events.list;
  }
  if (path.includes('/api/events') && method === 'POST') {
    return MOCK_DATA.events.createEvent;
  }

  // Plans/Subscription
  if (path.includes('/api/plans')) {
    return MOCK_DATA.plans.list;
  }
  if (path.includes('/api/subscriptions/create-checkout-session')) {
    return MOCK_DATA.subscription.checkoutSession;
  }

  // Product Catalog
  if (path.includes('/api/product-catalog/recommended')) {
    return MOCK_DATA.productCatalog.recommended;
  }
  if (path.includes('/api/product-catalog/search')) {
    return MOCK_DATA.productCatalog.catalog;
  }
  if (path.includes('/api/product-catalog')) {
    return MOCK_DATA.productCatalog.catalog;
  }

  // Products
  if (path.includes('/api/products/metadata')) {
    return {
      success: true,
      data: {
        categories: [
          { name: 'Boulangerie', icon: '🥖' },
          { name: 'Pâtisserie', icon: '🧁' },
          { name: 'Sandwichs', icon: '🥪' },
          { name: 'Plats', icon: '🍲' },
          { name: 'Boissons chaudes', icon: '☕' },
          { name: 'Boissons froides', icon: '🥤' },
        ],
        units: ['unité', 'pièce', 'kg'],
        activityTypes: [
          { name: 'bakery', label_fr: 'Boulangerie', icon: '🥖' },
        ],
      },
    };
  }
  if (path.includes('/api/products')) {
    // Convert to Product format with all required fields
    return {
      success: true,
      data: MOCK_DATA.products.list.map((p: any) => ({
        _id: String(p.id),
        id: String(p.id),
        name: p.name,
        category: p.category,
        purchasePrice: p.purchasePrice || 0.5,
        sellPrice: p.sellPrice || p.price,
        quantity: p.stock || 0,
        unit: 'pièce',
        productIcon: p.icon,
        popularity: p.popularity || 5,
        isAISuggested: p.isAISuggested || false,
        isCustom: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      })),
    };
  }

  // Sales (Kiosk)
  if (path.includes('/api/sales') && method === 'POST') {
    // Retourner la vente avec succès
    const saleData = body?.sale;
    return {
      success: true,
      sale: saleData,
      message: 'Vente enregistrée avec succès (mock)',
    };
  }

  // ═══ Daily Sales (Phase 4 — Saisie ventes itinérant) ═══
  if (path.includes('/api/daily-sales/pending-events')) {
    return MOCK_DATA.dailySales.pendingEvents;
  }
  if (path.includes('/api/daily-sales/pending-dates')) {
    return [];
  }
  if (path.match(/\/api\/daily-sales\/[^/]+\/validate/) && method === 'POST') {
    return MOCK_DATA.dailySales.validateResponse;
  }
  if (path.includes('/api/daily-sales/draft')) {
    if (method === 'POST' || method === 'PUT') {
      return MOCK_DATA.dailySales.draftResponse;
    }
    // GET draft → null (no draft)
    return null;
  }
  if (path.includes('/api/daily-sales') && method === 'POST') {
    return MOCK_DATA.dailySales.createResponse;
  }
  if (path.includes('/api/daily-sales') && method === 'GET') {
    return { data: [], total: 0, page: 1, limit: 20 };
  }

  // ═══ Predictions (Phase 4 — Event predictions) ═══
  if (path.match(/\/api\/predictions\/event\/(.+)/)) {
    const eventId = path.split('/event/')[1];
    return (
      (MOCK_DATA as any).eventPredictions?.[eventId] || {
        _id: 'pred-default',
        userId: 'mock-user-id',
        date: new Date().toISOString().split('T')[0],
        eventId,
        eventName: '',
        predictedProducts: [],
        expectedContext: {},
        totalPredictedQuantity: 0,
        totalPredictedRevenue: 0,
        overallConfidence: 0,
        generatedAt: new Date().toISOString(),
        cached: false,
      }
    );
  }
  if (path.includes('/api/predictions')) {
    return MOCK_DATA.dashboard.predictions;
  }
  if (path.includes('/api/recommendations')) {
    return (MOCK_DATA as any).recommendations?.list || [];
  }

  // User profile
  if (path.includes('/api/users/me')) {
    return MOCK_DATA.user.profile;
  }

  // Location
  if (path.includes('/api/locations')) {
    return MOCK_DATA.locations.list;
  }

  return null;
}
